import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import {
    QrCode, Download, ArrowLeft, ShieldCheck, Calendar, Package,
    Wrench, AlertTriangle, MapPin, ChevronRight, Loader2, Info,
    Copy, Check, Printer, MessageCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import SignatureSection from "@/components/SignatureSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const MODEL_LABEL = { alfa: "Modelo Alfa", omega: "Modelo Ômega", custom: "Personalizado" };
const STATUS_LABEL = {
    active: "Operacional",
    in_service: "Em manutenção",
    retired: "Inactivo",
    lost: "Perdido / Roubado",
};
const STATUS_CLASS = {
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    in_service: "bg-amber-100 text-amber-700 border-amber-200",
    retired: "bg-zinc-100 text-zinc-600 border-zinc-200",
    lost: "bg-red-100 text-red-700 border-red-200",
};

const KIND_LABEL = {
    revision: "Revisão",
    repair: "Reparação",
    part_replacement: "Substituição de peça",
    other: "Outro",
};

export default function ContaCarrinhoDetalhe() {
    const { serial } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const publicUrl = typeof window !== "undefined"
        ? `${window.location.origin}/carrinho/${serial}`
        : `/carrinho/${serial}`;

    const qrUrl = `${API}/carts/${encodeURIComponent(serial)}/qr.png?size=14&public_base=${encodeURIComponent(typeof window !== "undefined" ? window.location.origin : "")}`;

    const reload = useCallback(() => {
        setLoading(true);
        api.get(`/carts/${encodeURIComponent(serial)}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                if (e?.response?.status === 404) setNotFound(true);
                else if (e?.response?.status === 403) {
                    toast.error("Este carrinho não lhe pertence.");
                    setNotFound(true);
                } else {
                    toast.error(formatApiError(e?.response?.data?.detail) || "Erro ao carregar carrinho.");
                }
            })
            .finally(() => setLoading(false));
    }, [serial]);

    useEffect(() => { reload(); }, [reload]);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            toast.success("Link do passaporte copiado.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Não foi possível copiar.");
        }
    };

    const downloadQrPng = async () => {
        try {
            const res = await fetch(qrUrl);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `NOVIFOOD-QR-${serial}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("QR descarregado.");
        } catch (e) {
            toast.error("Falha ao descarregar QR.");
        }
    };

    const downloadPdf = async () => {
        if (!data) return;
        setDownloading(true);
        try {
            // Fetch QR PNG as base64
            const res = await fetch(qrUrl);
            const blob = await res.blob();
            const qrB64 = await new Promise((resolve, reject) => {
                const fr = new FileReader();
                fr.onloadend = () => resolve(fr.result);
                fr.onerror = reject;
                fr.readAsDataURL(blob);
            });

            const eq = data.equipment;
            // A6 portrait: 105 x 148 mm
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a6" });
            const W = 105;

            // ---- Header (navy band) ----
            pdf.setFillColor(15, 23, 42); // #0F172A primary
            pdf.rect(0, 0, W, 22, "F");
            pdf.setTextColor(251, 191, 36); // secondary gold
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(14);
            pdf.text("NOVIFOOD", 8, 10);
            pdf.setFontSize(6);
            pdf.setFont("helvetica", "normal");
            pdf.setTextColor(255, 255, 255);
            pdf.text("Passaporte digital do carrinho", 8, 15);
            pdf.setFontSize(4.5);
            pdf.text("PT · novifood.pt", 8, 18);

            // ---- QR block ----
            pdf.addImage(qrB64, "PNG", 27, 28, 51, 51);

            // ---- Cart info ----
            pdf.setTextColor(15, 23, 42);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(11);
            pdf.text(eq.nickname || (MODEL_LABEL[eq.model] || eq.model), W / 2, 88, { align: "center" });

            pdf.setFont("courier", "bold");
            pdf.setFontSize(9);
            pdf.text(eq.serial_number, W / 2, 95, { align: "center" });

            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(6);
            pdf.setTextColor(90, 90, 90);
            const modelLine = `${MODEL_LABEL[eq.model] || eq.model}${eq.warranty_until ? ` · Garantia até ${eq.warranty_until}` : ""}`;
            pdf.text(modelLine, W / 2, 101, { align: "center" });

            // ---- Instructions ----
            pdf.setFillColor(245, 245, 244); // muted
            pdf.rect(6, 108, W - 12, 26, "F");
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(6.5);
            pdf.setTextColor(15, 23, 42);
            pdf.text("Como usar este QR", 8, 113);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(5.5);
            pdf.setTextColor(70, 70, 70);
            const instr = [
                "1. Aponte a câmara do telemóvel para o QR",
                "2. Abra a página do passaporte digital",
                "3. Entre para ver historial e pedir manutenção",
            ];
            instr.forEach((line, i) => pdf.text(line, 8, 118 + i * 4));

            // ---- Footer ----
            pdf.setFontSize(4.5);
            pdf.setTextColor(120, 120, 120);
            pdf.text(publicUrl, W / 2, 141, { align: "center" });

            pdf.save(`NOVIFOOD-Passaporte-${eq.serial_number}.pdf`);
            toast.success("PDF descarregado — pronto a imprimir e colar no carrinho.");
        } catch (e) {
            console.error(e);
            toast.error("Falha ao gerar o PDF.");
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="py-24 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (notFound || !data) {
        return (
            <div className="py-16 text-center max-w-xl mx-auto">
                <Package className="h-10 w-10 text-secondary mx-auto" />
                <h2 className="mt-4 font-heading text-2xl font-extrabold text-primary">Carrinho não encontrado</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                    Não localizámos nenhum carrinho <code className="font-mono">{serial}</code> associado à sua conta.
                </p>
                <Link to="/conta/equipamentos"
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-bold text-primary hover:bg-muted transition">
                    <ArrowLeft className="h-4 w-4" /> Voltar aos meus equipamentos
                </Link>
            </div>
        );
    }

    const eq = data.equipment;
    const logs = data.maintenance_logs || [];
    const parts = eq.assigned_parts || [];
    const isLost = !!eq.reported_lost;

    return (
        <div data-testid="conta-carrinho-detalhe" className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <Link to="/conta/equipamentos" className="text-xs uppercase tracking-wider font-bold text-primary/60 hover:text-primary inline-flex items-center gap-1">
                        <ArrowLeft className="h-3.5 w-3.5" /> Meus equipamentos
                    </Link>
                    <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-extrabold text-primary">
                        {eq.nickname || `Carrinho ${eq.serial_number}`}
                    </h2>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-sm text-primary/70 tracking-wider">{eq.serial_number}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_CLASS[eq.status] || STATUS_CLASS.active}`}>
                            {STATUS_LABEL[eq.status] || eq.status}
                        </span>
                    </div>
                </div>
            </div>

            {isLost && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-5 flex items-start gap-3" data-testid="lost-banner">
                    <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-bold text-red-900">
                            Este carrinho está reportado como perdido/roubado.
                        </p>
                        {eq.reported_lost_note && (
                            <p className="mt-1 text-xs text-red-900/80">{eq.reported_lost_note}</p>
                        )}
                        <p className="mt-2 text-xs text-red-900/70">
                            Se o recuperou, contacte a NOVIFOOD para reactivar o passaporte.
                        </p>
                    </div>
                </div>
            )}

            {/* MAIN GRID */}
            <div className="grid gap-6 lg:grid-cols-3">
                {/* QR CARD */}
                <section
                    data-testid="qr-card"
                    className="lg:col-span-1 rounded-2xl border border-border bg-white p-6 lg:sticky lg:top-24 self-start"
                >
                    <div className="flex items-center gap-2 text-primary">
                        <QrCode className="h-4 w-4" />
                        <span className="text-xs font-bold uppercase tracking-[0.16em]">QR do passaporte</span>
                    </div>
                    <div className="mt-4 aspect-square rounded-xl bg-white border-2 border-dashed border-primary/10 p-3 flex items-center justify-center">
                        <img
                            src={qrUrl}
                            alt={`QR do carrinho ${eq.serial_number}`}
                            data-testid="qr-image"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground leading-snug text-center">
                        Cole ou grave este QR no carrinho.
                        Quem escanear vê apenas info pública; para ver este passaporte é preciso ser proprietário.
                    </p>

                    <div className="mt-5 space-y-2">
                        <button
                            type="button"
                            onClick={downloadPdf}
                            disabled={downloading}
                            data-testid="btn-download-pdf"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition disabled:opacity-60"
                        >
                            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                            {downloading ? "A gerar..." : "Descarregar PDF (A6)"}
                        </button>
                        <button
                            type="button"
                            onClick={downloadQrPng}
                            data-testid="btn-download-png"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-bold text-primary hover:bg-muted transition"
                        >
                            <Download className="h-4 w-4" /> Descarregar PNG
                        </button>
                        <button
                            type="button"
                            onClick={copyLink}
                            data-testid="btn-copy-link"
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-xs font-bold text-primary/80 hover:bg-muted transition"
                        >
                            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                            {copied ? "Copiado" : "Copiar link público"}
                        </button>
                    </div>
                </section>

                {/* DETAILS + PARTS + LOGS */}
                <div className="lg:col-span-2 space-y-6">
                    {/* INFO */}
                    <section className="rounded-2xl border border-border bg-white p-6" data-testid="info-card">
                        <div className="flex items-center gap-2 text-primary">
                            <ShieldCheck className="h-4 w-4" />
                            <span className="text-xs font-bold uppercase tracking-[0.16em]">Informação do carrinho</span>
                        </div>
                        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
                            <InfoRow icon={Package} label="Modelo" value={MODEL_LABEL[eq.model] || eq.model} />
                            <InfoRow icon={Calendar} label="Data de compra" value={eq.purchase_date || "—"} />
                            <InfoRow icon={ShieldCheck} label="Garantia até" value={eq.warranty_until || "—"} />
                            <InfoRow icon={Wrench} label="Próxima manutenção" value={eq.next_maintenance_at || "—"} highlight={!!eq.next_maintenance_at} />
                        </dl>
                        {eq.notes && (
                            <div className="mt-5 rounded-lg bg-muted/60 border border-border p-3 flex gap-2 text-xs text-muted-foreground">
                                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                <p>{eq.notes}</p>
                            </div>
                        )}
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link
                                to="/conta/assistencia"
                                state={{ equipmentId: eq.id }}
                                data-testid="cta-assistencia"
                                className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-bold text-secondary-foreground hover:-translate-y-0.5 transition"
                            >
                                <Wrench className="h-4 w-4" /> Pedir manutenção
                            </Link>
                            {!isLost && (
                                <button
                                    type="button"
                                    onClick={() => setReportOpen(true)}
                                    data-testid="cta-report-lost"
                                    className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50 transition"
                                >
                                    <AlertTriangle className="h-4 w-4" /> Reportar como perdido
                                </button>
                            )}
                        </div>
                    </section>

                    {/* PARTS */}
                    <section className="rounded-2xl border border-border bg-white p-6" data-testid="parts-card">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-primary">
                                <Package className="h-4 w-4" />
                                <span className="text-xs font-bold uppercase tracking-[0.16em]">
                                    Peças e acessórios instalados
                                </span>
                            </div>
                            <span className="text-xs font-bold text-primary/50">{parts.length}</span>
                        </div>
                        {parts.length === 0 ? (
                            <p className="mt-4 text-sm text-muted-foreground">
                                Ainda não há peças associadas. A NOVIFOOD regista aqui o que foi entregue com o seu carrinho.
                            </p>
                        ) : (
                            <ul className="mt-4 divide-y divide-border">
                                {parts.map((p, idx) => (
                                    <li key={p.id || idx} className="py-3 flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-primary text-sm">{p.name}</div>
                                            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                                {p.sku && <span className="font-mono">SKU: {p.sku}</span>}
                                                {p.category && <span className="capitalize">{p.category}</span>}
                                                {p.installed_at && <span>Instalado {p.installed_at}</span>}
                                            </div>
                                            {p.notes && <p className="mt-1 text-xs text-muted-foreground/80">{p.notes}</p>}
                                        </div>
                                        <span className="text-sm font-bold text-primary shrink-0 rounded-full bg-muted px-2.5 py-0.5">
                                            {p.quantity || 1}×
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* SIGNATURE */}
                    <SignatureSection serial={serial} />

                    {/* MAINTENANCE LOGS */}
                    <section className="rounded-2xl border border-border bg-white p-6" data-testid="logs-card">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-primary">
                                <Wrench className="h-4 w-4" />
                                <span className="text-xs font-bold uppercase tracking-[0.16em]">
                                    Histórico de manutenção
                                </span>
                            </div>
                            <span className="text-xs font-bold text-primary/50">{logs.length}</span>
                        </div>
                        {logs.length === 0 ? (
                            <p className="mt-4 text-sm text-muted-foreground">
                                Sem revisões registadas. A NOVIFOOD irá registar cada intervenção aqui.
                            </p>
                        ) : (
                            <ul className="mt-5 relative">
                                {logs.map((l, idx) => (
                                    <li key={l.id || idx} className="relative pl-6 pb-5 last:pb-0">
                                        <span
                                            aria-hidden
                                            className="absolute left-0 top-1 h-3 w-3 rounded-full bg-secondary ring-4 ring-secondary/20"
                                        />
                                        {idx < logs.length - 1 && (
                                            <span aria-hidden className="absolute left-[5px] top-4 bottom-0 w-px bg-border" />
                                        )}
                                        <div className="text-xs font-bold uppercase tracking-wider text-primary/60">
                                            {KIND_LABEL[l.kind] || l.kind} · {l.performed_at}
                                        </div>
                                        <div className="mt-1 font-bold text-primary text-sm">{l.summary}</div>
                                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                                            {l.technician && <span>Técnico: {l.technician}</span>}
                                            {typeof l.cost_eur === "number" && (
                                                <span>{l.cost_eur.toFixed(2)} €</span>
                                            )}
                                        </div>
                                        {l.parts_replaced?.length > 0 && (
                                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                {l.parts_replaced.map((pn, i) => (
                                                    <span key={i} className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold text-primary/70">
                                                        {pn}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            </div>

            {reportOpen && (
                <ReportLostModal
                    serial={serial}
                    onClose={() => setReportOpen(false)}
                    onDone={() => { setReportOpen(false); reload(); }}
                />
            )}
        </div>
    );
}

function InfoRow({ icon: Icon, label, value, highlight = false }) {
    return (
        <div className="flex items-start gap-3">
            <div className={`mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full ${highlight ? "bg-secondary/25 text-primary" : "bg-primary/5 text-primary/80"}`}>
                <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
                <dt className="text-[10px] font-bold uppercase tracking-wider text-primary/50">{label}</dt>
                <dd className="mt-0.5 text-sm font-bold text-primary truncate">{value}</dd>
            </div>
        </div>
    );
}

function ReportLostModal({ serial, onClose, onDone }) {
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post(`/account/carts/${encodeURIComponent(serial)}/report-lost`, {
                note: note.trim() || null,
            });
            toast.success("Reporte registado. A NOVIFOOD vai contactá-lo.");
            onDone();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Não foi possível enviar.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            role="dialog"
            data-testid="owner-report-lost-modal"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            >
                <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    <h3 className="font-heading text-lg font-extrabold text-primary">
                        Reportar carrinho como perdido/roubado
                    </h3>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                    Serial: <span className="font-mono font-bold text-primary">{serial}</span>. Isto irá bloquear a página pública com aviso a quem escanear o QR.
                </p>
                <label className="block mt-4">
                    <span className="text-xs font-bold uppercase tracking-wide text-primary/70">
                        Detalhes (opcional)
                    </span>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={4}
                        placeholder="Onde e quando desapareceu, etc."
                        data-testid="owner-report-note"
                        className="mt-1 w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-primary"
                    />
                </label>
                <div className="mt-6 flex justify-end gap-3">
                    <button type="button" onClick={onClose} className="inline-flex rounded-full border border-border px-4 py-2 text-sm font-bold text-primary hover:bg-muted transition">
                        Cancelar
                    </button>
                    <button type="submit" disabled={submitting} data-testid="owner-report-submit"
                        className="inline-flex rounded-full bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700 transition disabled:opacity-60">
                        {submitting ? "A enviar..." : "Reportar"}
                    </button>
                </div>
            </form>
        </div>
    );
}
